import { useRef, useState } from 'react';
import { Button, Checkbox, Input, Switch, Tag, Typography } from 'antd';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import {
  isExcludeKeywordField,
  isPrimaryKeywordField,
  keywordLimit,
  normalizeKeywordValue,
  showsMandatoryStars,
} from './keywordCriteria';

const { TextArea } = Input;
const { Text } = Typography;

const DEFAULT_BOOLEAN_PLACEHOLDER = '(Java OR J2EE) AND "Spring Boot" NOT Hibernate';

// One control, three roles, chosen by config rather than by three components:
//
//   primary  — owns the section header, the Boolean toggle and the expression box
//   include  — chips, and stars when the admin turned them on for this field
//   exclude  — the same chips with stars suppressed (a mandatory exclusion is
//              meaningless), attached to another field's group by keywordGroup
//
// Which of those a field is comes entirely from its admin config, so turning
// mandatory/exclude on for a new field later is a config change, not a code one.
export default function KeywordsField({
  field,
  value,
  onChange,
  disabled,
  placeholder,
  booleanEnabled,
  booleanMode,
  onBooleanModeChange,
  serverError,
}) {
  const model = normalizeKeywordValue(value);
  const isPrimary = isPrimaryKeywordField(field);
  const showStars = showsMandatoryStars(field);
  const isExclude = isExcludeKeywordField(field);
  const limit = keywordLimit(field);
  const expressionMode = isPrimary && booleanEnabled && booleanMode;

  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const inputRef = useRef(null);

  const emit = (patch) => onChange({ ...model, ...patch });

  // Every write recomputes mandatoryAll from the terms themselves, so the
  // "mark all" checkbox can never visibly disagree with the stars — the same
  // derived-state reasoning as the synthetic "All" checkbox in the checkbox
  // renderer (fieldTypeRegistry.jsx).
  const emitTerms = (terms) => emit({
    terms,
    mandatoryAll: terms.length > 0 ? terms.every((term) => term.mandatory) : model.mandatoryAll,
  });

  const commitDraft = (text) => {
    const candidates = String(text ?? '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (!candidates.length) {
      setDraft('');
      return;
    }

    const next = [...model.terms];
    const seen = new Set(next.map((term) => term.value.toLowerCase()));
    let capped = false;

    candidates.forEach((candidate) => {
      const key = candidate.toLowerCase();
      if (seen.has(key)) return;
      if (next.length >= limit) { capped = true; return; }
      seen.add(key);
      // A new chip inherits "mark all", so ticking the box then typing keeps
      // giving mandatory keywords instead of silently reverting to optional.
      next.push({ value: candidate, mandatory: showStars && model.mandatoryAll });
    });

    setNotice(capped ? `At most ${limit} keywords.` : '');
    setDraft('');
    if (next.length !== model.terms.length) emitTerms(next);
  };

  const removeTerm = (index) => {
    setNotice('');
    emitTerms(model.terms.filter((_, position) => position !== index));
  };

  const toggleStar = (index) => {
    emitTerms(model.terms.map((term, position) => (
      position === index ? { ...term, mandatory: !term.mandatory } : term
    )));
  };

  const handleMarkAll = (checked) => {
    onChange({
      ...model,
      mandatoryAll: checked,
      terms: model.terms.map((term) => ({ ...term, mandatory: checked })),
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft(draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && model.terms.length) {
      removeTerm(model.terms.length - 1);
    }
  };

  const clearAll = () => {
    setNotice('');
    setDraft('');
    onChange({ ...model, terms: [], mandatoryAll: false });
  };

  const header = isPrimary && (
    <div className="sbv1-keywords-header">
      <Text strong className="sbv1-keywords-title">{field.label ?? 'Keywords'}</Text>
      {booleanEnabled && (
        <span className="sbv1-keywords-toggle">
          <Switch
            size="small"
            checked={Boolean(booleanMode)}
            disabled={disabled}
            onChange={onBooleanModeChange}
          />
          <Text type="secondary">{booleanMode ? 'Boolean on' : 'Boolean off'}</Text>
        </span>
      )}
    </div>
  );

  if (expressionMode) {
    return (
      <div className="sbv1-keywords">
        {header}
        {/* autoSize rather than a fixed row count: the box grows with the
            expression, same as the chip box grows with its chips. */}
        <TextArea
          autoSize={{ minRows: field.rows ?? 3, maxRows: 12 }}
          value={model.expression}
          disabled={disabled}
          status={serverError ? 'error' : undefined}
          placeholder={field.booleanPlaceholder ?? DEFAULT_BOOLEAN_PLACEHOLDER}
          onChange={(event) => emit({ expression: event.target.value })}
        />
        {serverError && <div className="v1-input-validator-hint">{serverError}</div>}
      </div>
    );
  }

  return (
    <div className="sbv1-keywords">
      {header}
      <div className="sbv1-keywords-row">
        {/* Clicking anywhere in the box focuses the input, so the whole area is
            the target rather than just the caret-sized gap after the chips. */}
        <div
          className={`sbv1-keywords-box${disabled ? ' sbv1-keywords-box-disabled' : ''}`}
          onClick={() => inputRef.current?.focus()}
          role="presentation"
        >
          {model.terms.map((term, index) => (
            <Tag
              key={term.value}
              className="sbv1-keywords-chip"
              closable={!disabled}
              onClose={(event) => { event.preventDefault(); removeTerm(index); }}
            >
              {showStars && (
                <button
                  type="button"
                  className="sbv1-keywords-star"
                  aria-pressed={term.mandatory}
                  aria-label={term.mandatory
                    ? `${term.value} is mandatory` : `Mark ${term.value} as mandatory`}
                  disabled={disabled}
                  onClick={(event) => { event.stopPropagation(); toggleStar(index); }}
                >
                  {term.mandatory ? <StarFilled /> : <StarOutlined />}
                </button>
              )}
              <span className="sbv1-keywords-chip-text">{term.value}</span>
            </Tag>
          ))}
          <input
            ref={inputRef}
            className="sbv1-keywords-input"
            value={draft}
            disabled={disabled}
            placeholder={model.terms.length
              ? (field.placeholder ?? placeholder ?? 'Type another keyword')
              : (placeholder ?? field.placeholder ?? 'Type another keyword')}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            // Committed on blur too, so a keyword typed but never Entered is
            // not silently dropped when the user goes straight to Search.
            onBlur={() => commitDraft(draft)}
          />
        </div>
        {model.terms.length > 0 && !disabled && (
          <Button type="link" size="small" className="sbv1-keywords-clear" onClick={clearAll}>
            Clear all
          </Button>
        )}
      </div>

      {notice && <div className="v1-input-validator-hint">{notice}</div>}

      {showStars && !isExclude && (
        <Checkbox
          className="sbv1-keywords-mandatory"
          // Derived from the chips whenever there are any, so starring them all
          // by hand ticks the box and unstarring one unticks it. Falls back to
          // the stored flag only while the box is empty, where there is nothing
          // to derive from but the choice still has to apply to the next chip.
          checked={model.terms.length ? model.terms.every((term) => term.mandatory) : model.mandatoryAll}
          disabled={disabled}
          onChange={(event) => handleMarkAll(event.target.checked)}
        >
          Mark all keywords as mandatory
        </Checkbox>
      )}
    </div>
  );
}

KeywordsField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  // Whether the form offers Boolean at all (schema.booleanSearchEnabled). Only
  // the primary field draws the switch, so a form without one shows none.
  booleanEnabled: PropTypes.bool,
  booleanMode: PropTypes.bool,
  onBooleanModeChange: PropTypes.func,
  serverError: PropTypes.string,
};
